import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { RouterProvider, createBrowserRouter, type RouteObject } from 'react-router';
import {
  DestinationGate,
  LoginShell,
  SigningIn,
  signingInTitle,
  PRODUCT_TAGLINES,
  ProductLockup,
  StatusView,
  useAppearance,
  type DestinationSession,
} from '@traceio-org/auth-ui';
import type { TraceEnvironment } from '@traceio-org/platform-contracts';
import { createAdminApi, type AdminApi } from './api';
import type { AdminConfig } from './config';
import { AdminProvider } from './context';
import type { AdminDependencies } from './deps';
import { ADMIN_PRODUCT_NAME } from './navigation';
import { checkAdminRegistration } from './registry';
import { createAdminRoutes } from './routes';

type Router = ReturnType<typeof createBrowserRouter>;

/** Builds the router; tests pass createMemoryRouter with their own entries. */
export type RouterFactory = (routes: RouteObject[]) => Router;

function useHeading(title: string) {
  const ref = useRef<HTMLHeadingElement>(null);
  useEffect(() => {
    document.title = `${title} | Trace`;
    ref.current?.focus({ preventScroll: true });
  }, [title]);
  return ref;
}

/** The sign-in layout of the shared auth UI, used before staff access is confirmed. */
function Frame({ children }: { children: ReactNode }) {
  return (
    <LoginShell brand={<ProductLockup productKey="admin" surface="dark" />} tagline={PRODUCT_TAGLINES.admin}>
      {children}
    </LoginShell>
  );
}

/** Rendered instead of Trace Admin when its settings or the registry forbid it to run: no gate, no token. */
export function RefusedPage({ errors }: { errors: string[] }) {
  const title = `${ADMIN_PRODUCT_NAME} cannot start`;
  const headingRef = useHeading(title);
  return (
    <Frame>
      <StatusView tone="error" title={title} headingRef={headingRef}>
        <p>Its configuration does not match where it runs:</p>
        <ul className="trace-auth-config-errors">
          {errors.map((error) => (
            <li key={error}>{error}</li>
          ))}
        </ul>
      </StatusView>
    </Frame>
  );
}

function Loading() {
  // The first screen of a sign-in: the same heading as the static shell before it and DestinationGate
  // after it (SigningIn), so loading the registry does not add a heading of its own between them.
  const title = signingInTitle(ADMIN_PRODUCT_NAME);
  const headingRef = useHeading(title);
  return (
    <Frame>
      <SigningIn title={title} headingRef={headingRef} />
    </Frame>
  );
}

function RegistryFailed({ onRetry }: { onRetry: () => void }) {
  const title = 'We could not load the Trace application registry';
  const headingRef = useHeading(title);
  return (
    <Frame>
      <StatusView
        tone="error"
        title={title}
        headingRef={headingRef}
        primaryAction={{ label: 'Try again', onClick: onRetry }}
      >
        <p>
          Trace Admin checks that platform-api registers it before it opens. Check that platform-api is
          running, then try again.
        </p>
      </StatusView>
    </Frame>
  );
}

/**
 * The staff application, mounted by DestinationGate only once GET /v1/admin/me confirmed active staff with
 * MFA. The router is created here, after the gate restored the return path, so the first page is the one
 * the person asked for.
 */
function AdminRoot({
  api,
  hubOrigin,
  assetBaseUrl,
  environment,
  createRouter,
}: {
  api: AdminApi;
  hubOrigin: string;
  assetBaseUrl: string;
  environment: TraceEnvironment;
  createRouter: RouterFactory;
}) {
  const [router] = useState(() => createRouter(createAdminRoutes()));
  const value = useMemo(
    () => ({ api, hubOrigin, assetBaseUrl, environment }),
    [api, hubOrigin, assetBaseUrl, environment],
  );
  return (
    <AdminProvider value={value}>
      <RouterProvider router={router} />
    </AdminProvider>
  );
}

type Boot =
  | { kind: 'loading' }
  | { kind: 'failed' }
  | { kind: 'refused'; errors: string[] }
  | { kind: 'ready'; audience: string; session: DestinationSession; api: AdminApi };

export interface AdminAppProps {
  config: AdminConfig;
  deps: AdminDependencies;
  createRouter?: RouterFactory;
}

/**
 * Trace Admin: loads the application registry and starts only where it registers Trace Admin for this
 * environment and hub (src/registry.ts), then wraps everything in DestinationGate with productKey admin and
 * the registry's admin audience. The gate sends people without a session to the hub's /login/admin and
 * back, sends non-staff to the hub's access-denied page and staff without MFA to sign in again; nothing
 * of Trace Admin renders before platform-api confirmed staff access.
 */
export function AdminApp({ config, deps, createRouter = createBrowserRouter }: AdminAppProps) {
  const [boot, setBoot] = useState<Boot>({ kind: 'loading' });
  const [attempt, setAttempt] = useState(0);
  // The stored appearance on every screen, the sign-in frames included, following the device while it is System.
  useAppearance(config.hubOrigin);

  useEffect(() => {
    let active = true;
    deps.client.applications().then(
      (registry) => {
        if (!active) return;
        const registration = checkAdminRegistration(registry, config, deps.origin);
        if (!registration.ok) {
          setBoot({ kind: 'refused', errors: registration.errors });
          return;
        }
        const session = deps.createSession(registration.audience);
        setBoot({
          kind: 'ready',
          audience: registration.audience,
          session,
          api: createAdminApi({ client: deps.client, session, audience: registration.audience }),
        });
      },
      (error: unknown) => {
        if (!active) return;
        console.warn('admin-web: application registry unavailable', error);
        setBoot({ kind: 'failed' });
      },
    );
    return () => {
      active = false;
    };
  }, [config, deps, attempt]);

  switch (boot.kind) {
    case 'loading':
      return <Loading />;
    case 'failed':
      return (
        <RegistryFailed
          onRetry={() => {
            setBoot({ kind: 'loading' });
            setAttempt((n) => n + 1);
          }}
        />
      );
    case 'refused':
      return <RefusedPage errors={boot.errors} />;
    case 'ready':
      return (
        <DestinationGate
          productKey="admin"
          session={boot.session}
          client={deps.client}
          hubOrigin={config.hubOrigin}
          audience={boot.audience}
          {...(deps.assign ? { assign: deps.assign } : {})}
        >
          <AdminRoot
            api={boot.api}
            hubOrigin={config.hubOrigin}
            assetBaseUrl={deps.client.baseUrl}
            environment={config.environment}
            createRouter={createRouter}
          />
        </DestinationGate>
      );
  }
}
